import {
  Box, Typography, IconButton, Tooltip, TextField, LinearProgress,
  Dialog, DialogContent, DialogTitle, DialogActions, Button,
} from '@mui/material';
import { CloudUpload, Delete, Download, Crop, Edit, Visibility, PhotoLibrary, Warning } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useState } from 'react';
import type { Photo } from '../../../types/photo';
import { downloadSinglePhoto } from '../../../services/photoService';

export interface SlotUploadState { progress: number; status: 'idle' | 'uploading' | 'confirming' | 'error'; step?: string; }

export function FilledSlot({ photo, theme, onDelete, onRecrop, onEditCropped, onUpdateComment }: {
  photo: Photo; theme: any;
  onDelete?: () => void; onRecrop?: () => void; onEditCropped?: () => void; onUpdateComment?: (c: string | null) => void;
}) {
  const [editingComment, setEditingComment] = useState(false);
  const [commentDraft, setCommentDraft] = useState(photo.Comment || '');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewTitle, setPreviewTitle] = useState('');

  return (
    <Box sx={{ borderRadius: 1.5, overflow: 'hidden',
      border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}` }}>
      <Box sx={{ position: 'relative' }}>
        <Box component="img" src={photo.thumbnailUrl || photo.imageUrl} alt={photo.FileName} loading="lazy"
          onError={(e: any) => { if (photo.thumbnailUrl && e.target.src !== photo.imageUrl) e.target.src = photo.imageUrl; }}
          sx={{ width: '100%', height: 280, objectFit: 'contain', display: 'block', bgcolor: alpha(theme.palette.text.primary, 0.03) }} />
        {photo.ProcessingStatus === 'failed' && (
          // REQ-2026-0009: pair the warning badge with a "Crop manually" CTA so
          // users have an obvious next step when auto-crop can't lock onto the
          // specimen (and so the manual fallback is one click away).
          // BUG-2026-0205: say what actually happened — the photo is kept
          // exactly as uploaded. Photos that were already cropped offline
          // (e.g. the lab script's result#.jpg, which shows only one of the
          // three green markers) land here and need no further action.
          <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Tooltip title="Auto-crop couldn't find the scale markers, so this photo is kept exactly as uploaded. If it was already cropped, nothing else is needed — otherwise click 'Crop manually' to position the crop yourself.">
              <Box sx={{ bgcolor: 'warning.main', color: 'warning.contrastText',
                borderRadius: 1, px: 0.8, py: 0.3, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Warning sx={{ fontSize: 14 }} />
                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem' }}>Not auto-cropped</Typography>
              </Box>
            </Tooltip>
            {onRecrop && (
              <Button
                size="small"
                variant="contained"
                color="warning"
                startIcon={<Crop sx={{ fontSize: 14 }} />}
                onClick={onRecrop}
                sx={{
                  textTransform: 'none', fontSize: '0.65rem', fontWeight: 700,
                  px: 1, py: 0.3, minHeight: 0, lineHeight: 1.2,
                  boxShadow: 'none',
                  '&:hover': { boxShadow: 1 },
                }}
              >
                Crop manually
              </Button>
            )}
          </Box>
        )}
      </Box>

      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 1, py: 0.3, bgcolor: alpha(theme.palette.text.primary, 0.04),
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
      }}>
        <Box sx={{ display: 'flex', gap: 0.3 }}>
          {onRecrop && <Tooltip title="Re-crop from original"><IconButton size="small" onClick={onRecrop}><Crop fontSize="small" /></IconButton></Tooltip>}
          {onEditCropped && <Tooltip title="Edit cropped"><IconButton size="small" onClick={onEditCropped}><Edit fontSize="small" /></IconButton></Tooltip>}
          <Tooltip title="View cropped"><IconButton size="small" onClick={() => { setPreviewUrl(photo.imageUrl); setPreviewTitle('Cropped'); }}><Visibility fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="View original"><IconButton size="small" onClick={() => { setPreviewUrl(photo.originalImageUrl || photo.imageUrl); setPreviewTitle('Original'); }}><PhotoLibrary fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Download"><IconButton size="small" onClick={() => downloadSinglePhoto(photo.imageUrl, photo.FileName)}><Download fontSize="small" /></IconButton></Tooltip>
        </Box>
        {onDelete && (
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => setConfirmDelete(true)}><Delete fontSize="small" /></IconButton>
          </Tooltip>
        )}
      </Box>

      <Box sx={{ px: 1, py: 0.5, bgcolor: alpha(theme.palette.background.default, 0.9) }}>
        {editingComment && onUpdateComment ? (
          <TextField size="small" fullWidth variant="standard" value={commentDraft} autoFocus placeholder="Add comment..."
            onChange={(e) => setCommentDraft(e.target.value)}
            onBlur={() => { onUpdateComment(commentDraft || null); setEditingComment(false); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { onUpdateComment(commentDraft || null); setEditingComment(false); } }}
            inputProps={{ style: { fontSize: '0.75rem' } }} />
        ) : (
          <Typography variant="caption" sx={{ cursor: onUpdateComment ? 'pointer' : 'default', color: photo.Comment ? 'text.secondary' : 'text.disabled' }}
            onClick={onUpdateComment ? () => setEditingComment(true) : undefined}>
            {photo.Comment || (onUpdateComment ? 'Click to add comment...' : '')}
          </Typography>
        )}
      </Box>

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} maxWidth="xs">
        <DialogTitle>Delete Photo?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">This will permanently remove the photo from this slot. This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button color="error" variant="contained" sx={{ textTransform: 'none' }}
            onClick={() => { setConfirmDelete(false); onDelete(); }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!previewUrl} onClose={() => setPreviewUrl(null)} maxWidth="lg">
        <DialogTitle>{previewTitle}</DialogTitle>
        <DialogContent sx={{ p: 1 }}>
          {previewUrl && <Box component="img" src={previewUrl} alt={previewTitle}
            sx={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain', display: 'block', mx: 'auto' }} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewUrl(null)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

const STEP_LABEL: Record<string, string> = {
  uploading: 'Uploading...', processing: 'Auto-cropping...', saving: 'Saving...', confirming: 'Finalizing...',
};

export function EmptySlot({ theme, uploadState, onDrop, onClickUpload }: {
  theme: any; uploadState?: SlotUploadState;
  onDrop: (e: React.DragEvent) => void; onClickUpload: () => void;
}) {
  const [dragOver, setDragOver] = useState(false);
  const isUploading = uploadState?.status === 'uploading' || uploadState?.status === 'confirming';

  return (
    <Box
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { setDragOver(false); onDrop(e); }}
      onClick={isUploading ? undefined : onClickUpload}
      sx={{
        height: 280, borderRadius: 1.5, cursor: isUploading ? 'default' : 'pointer',
        border: `2px dashed ${dragOver ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.15)}`,
        bgcolor: dragOver ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        transition: 'all 0.2s', gap: 0.5,
        '&:hover': { borderColor: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.02) },
      }}>
      {isUploading ? (
        <Box sx={{ width: '80%', textAlign: 'center' }}>
          <LinearProgress
            variant={uploadState!.progress > 0 ? 'determinate' : 'indeterminate'}
            value={uploadState!.progress}
            sx={{ mb: 0.5, borderRadius: 1 }}
          />
          <Typography variant="caption" color="text.secondary">
            {STEP_LABEL[uploadState!.step || uploadState!.status] || `${uploadState!.progress}%`}
          </Typography>
        </Box>
      ) : (
        <>
          <CloudUpload sx={{ fontSize: 28, color: alpha(theme.palette.text.primary, 0.25) }} />
          <Typography variant="caption" color="text.disabled">Drop or click</Typography>
        </>
      )}
      {uploadState?.status === 'error' && <Typography variant="caption" color="error">Upload failed</Typography>}
    </Box>
  );
}
